import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CongregationCampaignTable } from '@/tables/CongregationCampaignTable';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

/**
 * `?created` carries the "Campaign added successfully" confirmation from the
 * add form, which legacy also shows on the page it redirects to.
 */
interface CampaignIndexSearch {
  created?: boolean;
}

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/campaign/',
)({
  validateSearch: (search: Record<string, unknown>): CampaignIndexSearch => ({
    created: search.created === true || search.created === 'true' || undefined,
  }),
  component: CongregationCampaignScreen,
});

/**
 * The congregation arm of the Campaigns tab, for admins and mods of
 * congregations and custom study groups.
 *
 * Legacy hides the tab rather than returning 403, so a refused caller is
 * redirected to the group dashboard.
 */
function CongregationCampaignScreen(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = Route.useParams();
  const { created } = Route.useSearch();
  const { isCoordinator, isResolved } = useCoordinatorAccess();
  const refused = isResolved && !isCoordinator;

  // Held in state and the param stripped, so the confirmation shows once rather
  // than on every reload.
  const [showCreated, setShowCreated] = useState(Boolean(created));

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  useEffect(() => {
    if (created) {
      setShowCreated(true);
      navigate({
        to: '/groups/$groupName/campaign/',
        params: { groupName },
        search: {},
        replace: true,
      });
    }
  }, [created, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('sidebar.campaigns')}
          placement="group_campaigns"
        />
        <WBSButton
          wide
          onClick={() =>
            navigate({
              to: '/groups/$groupName/campaign/add/',
              params: { groupName },
            })
          }
        >
          {t('actions.add-new-campaign')}
        </WBSButton>
      </GroupHeaderWrapper>
      {showCreated && (
        <AlertBox variant="success">
          {t('wbs.campaign-added-successfully')}
        </AlertBox>
      )}
      <CongregationCampaignTable />
    </>
  );
}
